import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Grid, Card, CardContent, Chip, IconButton,
  Tooltip, Paper, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

// Map role → display type label for the table
const roleToTypeLabel = (role) => {
  switch (role) {
    case 'partner':
      return 'Corporate';
    case 'individual_agent':
      return 'Agent';
    case 'free_user':
      return 'Referral';
    default:
      return role || 'Other';
  }
};

const codeColumns = [
  { field: 'code', headerName: 'Code', width: 180 },
  {
    field: 'type', headerName: 'Type', width: 120,
    renderCell: (row) => (
      <Chip
        label={row.type}
        size="small"
        sx={{
          fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px',
          bgcolor: row.type === 'Agent' ? '#E3F2FD' : row.type === 'Corporate' ? '#F3E5F5' : row.type === 'Campaign' ? '#E0F2F1' : '#FFF8E1',
          color: row.type === 'Agent' ? '#1565C0' : row.type === 'Corporate' ? '#7B1FA2' : row.type === 'Campaign' ? '#00695C' : '#F57F17',
        }}
      />
    ),
  },
  { field: 'owner', headerName: 'Owner', width: 220 },
  {
    field: 'status', headerName: 'Status', width: 120,
    renderCell: (row) => <StatusBadge status={row.status} />,
  },
  { field: 'totalLeads', headerName: 'Total Leads', width: 120, align: 'right' },
  { field: 'createdDate', headerName: 'Created Date', width: 140 },
  {
    field: 'actions', headerName: 'Actions', width: 150, sortable: false,
    renderCell: () => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Copy Code">
          <IconButton size="small"><ContentCopyIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small"><EditIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      </Box>
    ),
  },
];

export default function ReferralManagement() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'Agent', owner: '', expiryDate: '' });
  const [refreshKey] = useState(0);

  // Live referral codes: compute from users that have a referralCode
  const referralCodes = useMemo(() => {
    const users = db.users.getAll().filter((u) => u.referralCode);
    return users.map((u) => ({
      id: `RC-${u.id}`,
      code: u.referralCode,
      type: roleToTypeLabel(u.role),
      owner: u.name,
      status: u.status === 'active' ? 'Active' : 'Inactive',
      totalLeads: db.cases.getByReferralOwnerId(u.id).length,
      createdDate: u.joinDate || '',
    }));
    // eslint-disable-next-line
  }, [user, refreshKey]);

  // Live campaigns
  const campaigns = useMemo(() => db.campaigns.getAll(), [refreshKey]);

  // Live partners list for the owner dropdown in the create dialog
  const partnerOptions = useMemo(
    () => db.users.getAll().filter((u) => u.role === 'partner' || u.role === 'individual_agent'),
    [refreshKey],
  );

  const filteredCodes = useMemo(() => {
    if (tab === 0) return referralCodes;
    if (tab === 1) return referralCodes; // Campaigns tab uses its own view below
    if (tab === 2) return referralCodes.filter((r) => r.type === 'Agent' || r.type === 'Corporate');
    return referralCodes;
  }, [tab, referralCodes]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
          Referral Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: PRIMARY, borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#142847' } }}
          >
            Create Referral Code
          </Button>
          <Button
            variant="outlined"
            startIcon={<QrCode2Icon />}
            onClick={() => setQrOpen(true)}
            sx={{ borderColor: SECONDARY, color: SECONDARY, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Generate QR Code
          </Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          TabIndicatorProps={{ sx: { bgcolor: PRIMARY } }}
        >
          <Tab label="All Codes" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Campaigns" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="QR Codes" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Links" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {/* Main Table */}
      {tab !== 1 ? (
        filteredCodes.length > 0 ? (
          <DataTable
            title="Referral Codes"
            columns={codeColumns}
            rows={filteredCodes}
            searchable
            exportable
          />
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>No data yet</Alert>
        )
      ) : (
        /* Campaigns Tab */
        campaigns.length > 0 ? (
          <Grid container spacing={3}>
            {campaigns.map((camp) => (
              <Grid item xs={12} sm={6} md={4} key={camp.id}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY }}>
                        {camp.name}
                      </Typography>
                      <StatusBadge status={camp.status === 'active' ? 'Active' : camp.status === 'ended' ? 'Expired' : 'Pending'} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={camp.code}
                        size="small"
                        icon={<LinkIcon sx={{ fontSize: 14 }} />}
                        sx={{ fontWeight: 600, bgcolor: '#F5F7FA', mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {camp.startDate} to {camp.endDate}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Leads</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>{camp.totalLeads}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Conversions</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: SECONDARY }}>{camp.conversions}</Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}>
                        View Details
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<QrCode2Icon sx={{ fontSize: 14 }} />} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}>
                        QR Code
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>No data yet</Alert>
        )
      )}

      {/* Create Referral Code Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>Create Referral Code</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Referral Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              fullWidth
              placeholder="e.g., REF-PARTNER-001"
              size="small"
            />
            <TextField
              label="Type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              fullWidth
              select
              size="small"
            >
              <MenuItem value="Agent">Agent</MenuItem>
              <MenuItem value="Corporate">Corporate</MenuItem>
              <MenuItem value="Campaign">Campaign</MenuItem>
              <MenuItem value="Referral">Referral</MenuItem>
            </TextField>
            <TextField
              label="Owner"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              fullWidth
              select
              size="small"
            >
              {partnerOptions.map((p) => (
                <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Expiry Date"
              type="date"
              value={form.expiryDate}
              onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setCreateOpen(false)}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#142847' } }}
          >
            Create Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate QR Code Dialog */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>Generate QR Code</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box
              sx={{
                width: 200, height: 200, mx: 'auto', mb: 2, borderRadius: 2,
                border: '2px dashed #CBD5E1', display: 'flex', alignItems: 'center',
                justifyContent: 'center', bgcolor: '#F8FAFC',
              }}
            >
              <QrCode2Icon sx={{ fontSize: 120, color: PRIMARY, opacity: 0.3 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a referral code to generate its QR code
            </Typography>
            <TextField
              select
              fullWidth
              label="Select Referral Code"
              size="small"
              defaultValue=""
            >
              {referralCodes.filter((r) => r.status === 'Active').map((r) => (
                <MenuItem key={r.id} value={r.code}>{r.code} - {r.owner}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQrOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: SECONDARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#00a383' } }}
          >
            Generate & Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
